<template>
  <PageTemplate>
    <div class="d-flex justify-content-between">
      <h6 class="element-header">
        {{ $t('holiday.title') }}
      </h6>
    </div>

    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="element-box">
        <h5 class="form-header mb-4">
          {{ $t('holiday.overview') }}
        </h5>

        <div v-if="!overviewUserId">
          <div class="row mb-4 d-flex justify-content-end">
            <!-- Regular holiday form  -->
            <div class="pe-4">
              <CreateButton :label="$t('holiday.apply_for_vacation')" @click="handleRegularHolidayForm"/>
            </div>

            <!-- Special leave form  -->
            <div class="pe-4">
              <CreateButton :label="$t('holiday.apply_for_special_leave')" @click="handleSpecialLeaveForm"/>
            </div>

            <!-- Parental leave form  -->
            <div class="pe-2">
              <CreateButton :label="$t('holiday.apply_for_parental_leave')" @click="handleParentalLeaveForm"/>
            </div>
          </div>
        </div>

        <UserSummaryData :data="userDetails"/>

        <span v-if="overviewUserId">
          <OverviewUserHolidayList
              :holidays="holidays"
              :overviewUserId="overviewUserId"
              :yearsDropdown="yearsDropdown"
              :loading="isSearching"
              :filters="filters"
              @search="getUserHolidays"/>
        </span>
        <span v-else>
          <UserHolidayList
              :holidays="holidays"
              :yearsDropdown="yearsDropdown"
              :loading="isSearching"
              :filters="filters"
              v-model:showRegularHolidayModal="showRegularHolidayModal"
              v-model:regularHolidayId="regularHolidayId"
              v-model:showSpecialLeaveModal="showSpecialLeaveModal"
              v-model:specialLeaveId="specialLeaveId"
              v-model:showParentalLeaveModal="showParentalLeaveModal"
              v-model:parentalLeaveId="parentalLeaveId"
              v-model:showDeleteModal="showDeleteModal"
              v-model:requestIdForDeleting="requestIdForDeleting"
              @search="getUserHolidays"/>
        </span>
      </div>

      <Modal
          v-model="showRegularHolidayModal"
          :title="$t('holiday.apply_for_vacation')"
          :hide-cancel="true"
          :button-label="$t('general.button.save')"
          sizeModal="modal-lg"
          :hide-footer="true"
          classFooter="justify-content-center">
        <RegularHolidayForm
            :regularHolidayId="regularHolidayId"
            v-model:submitForm="submitForm"/>
      </Modal>

      <Modal
          v-model="showSpecialLeaveModal"
          :title="$t('holiday.apply_for_special_leave')"
          :hide-cancel="true"
          :button-label="$t('general.button.save')"
          :hide-footer="true"
          sizeModal="modal-lg"
          classFooter="justify-content-center">
        <SpecialLeaveForm
            :specialLeaveId="specialLeaveId"
            v-model:submitForm="submitForm"/>
      </Modal>

      <Modal
          v-model="showParentalLeaveModal"
          :title="$t('holiday.apply_for_parental_leave')"
          :hide-cancel="true"
          :button-label="$t('general.button.save')"
          :hide-footer="true"
          sizeModal="modal-lg"
          classFooter="justify-content-center">
        <ParentalLeaveForm
            :parentalLeaveId="parentalLeaveId"
            v-model:submitForm="submitForm"/>
      </Modal>

      <ModalDelete
          v-model="showDeleteModal"
          :header="t('general.delete_item_header')"
          :onDelete="deleteHolidayRequest">
      </ModalDelete>
    </div>
  </PageTemplate>
</template>

<script setup>
import holidayService from '@/api/Holiday';
import CreateButton from '@/components/CreateButton.vue';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/modal/Modal.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import ParentalLeaveForm from '@/views/App/Holiday/Overview/Form/ParentalLeaveForm.vue';
import RegularHolidayForm from '@/views/App/Holiday/Overview/Form/RegularHolidayForm.vue';
import SpecialLeaveForm from '@/views/App/Holiday/Overview/Form/SpecialLeaveForm.vue';
import OverviewUserHolidayList from '@/views/App/Holiday/Overview/List/OverviewUserHolidayList.vue';
import UserHolidayList from '@/views/App/Holiday/Overview/List/UserHolidayList.vue';
import UserSummaryData from '@/views/App/Holiday/Overview/UserSummaryData.vue';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const { t }                   = useI18n();
const route                   = useRoute();
const overviewUserId          = route.params.overviewUserId;
const yearsDropdown           = ref([]);
const userDetails             = ref({});
const holidays                = ref([]);
const isLoaded                = ref(false);
const isSearching             = ref(false);
const showRegularHolidayModal = ref(false);
const regularHolidayId        = ref(null);
const showSpecialLeaveModal   = ref(false);
const specialLeaveId          = ref(null);
const showParentalLeaveModal  = ref(false);
const parentalLeaveId         = ref(null);
const submitForm              = ref(false);
const showDeleteModal         = ref(false);
const requestIdForDeleting    = ref(null);

const filters = ref({
  year: '',
});

async function getUserDetails() {
  try {
    const { data }      = await holidayService.getUserDetails(overviewUserId);
    userDetails.value   = data.data.userDetails;
    yearsDropdown.value = data.data.distinctYears.map(item => ({
      label: item,
      value: item,
    }));
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getUserHolidays(passedFilters) {
  try {
    if (passedFilters) {
      isSearching.value = true;
    }


    const { data }    = await holidayService.getUserHolidays(overviewUserId, {
      year: filters.value.year,
    });
    holidays.value    = data.data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

watch(submitForm, () => {
  getUserHolidays();
  closeModals()
});

watch(requestIdForDeleting, () => {
  CloseToast();
});

function handleRegularHolidayForm() {
  CloseToast();
  setIdsToNull();
  showRegularHolidayModal.value = true;
}

function handleSpecialLeaveForm() {
  CloseToast();
  setIdsToNull();
  showSpecialLeaveModal.value = true;
}

function handleParentalLeaveForm() {
  CloseToast();
  setIdsToNull();
  showParentalLeaveModal.value = true;
}

async function deleteHolidayRequest() {
  try {
    await holidayService.deleteHolidayRequest(requestIdForDeleting.value);
    await getUserHolidays();

    showDeleteModal.value = false;
    setIdsToNull();
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

function setIdsToNull() {
  regularHolidayId.value     = null;
  specialLeaveId.value       = null;
  parentalLeaveId.value      = null;
  requestIdForDeleting.value = null;
}

function closeModals(){
  showRegularHolidayModal.value = false;
  showSpecialLeaveModal.value   = false;
  showParentalLeaveModal.value  = false;
  showDeleteModal.value         = false;
}

onMounted(async () => {
  CloseToast();
  await Promise.all([
    getUserDetails(),
    getUserHolidays(),
  ]);

  isLoaded.value = true;
});
</script>

<style scoped>

</style>